import React, { useState } from 'react';
import { getClient } from '../../../apollo';
import { Icon as LegacyIcon } from '@ant-design/compatible';
import { Button, Divider, Drawer, Table } from 'antd';
import VehicleCreateBrand from './VehicleCreateBrand';
import _ from 'lodash';
import VehicleCreateModel from './VehicleCreateModel';
import UpdateBrand from './VehicleUpdateBrand';
import VehicleUpdateModel from './VehicleUpdateModel';
import {
  GetVehicleBrandModelsDocument,
  GetVehicleBrandModelsQuery,
  GetVehicleBrandModelsQueryVariables,
  useDeleteVehicleBrandMutation,
  useDeleteVehicleModelMutation,
  useGetVehicleBrandModelsQuery,
} from '../../../graphql/datamodel.gen';

interface VehicleBrandModelsDrawerProps {
  visible: boolean;
  onClose: () => any;
}

const VehicleBrandModelsDrawer: React.FC<VehicleBrandModelsDrawerProps> = (
  props: VehicleBrandModelsDrawerProps,
) => {
  const [createBrand, setCreateBrand] = useState<boolean>(false);
  const [updateBrand, setUpdateBrand] = useState<boolean>(false);
  const [createModel, setCreateModel] = useState<boolean>(false);
  const [updateModel, setUpdateModel] = useState<boolean>(false);
  const [selectedBrand, setSelectedBrand] = useState<string>(undefined);
  const [selectedModel, setSelectedModel] = useState<string>(undefined);

  const vehicleBrandModelsQueryResult = useGetVehicleBrandModelsQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
  });

  const [vehicleDeleteBrandMutation] = useDeleteVehicleBrandMutation({
    client: getClient(),
  });

  const [vehicleDeleteModelMutation] = useDeleteVehicleModelMutation({
    client: getClient(),
  });

  const deleteModel = async (id: string) => {
    return vehicleDeleteModelMutation({
      variables: {
        id,
      },
      update: (proxy, mutationResult) => {
        const deleted = mutationResult.data.deleteVehicleModel?.deleted;
        if (deleted) {
          const previousData = getClient().readQuery<
            GetVehicleBrandModelsQuery,
            GetVehicleBrandModelsQueryVariables
          >({
            query: GetVehicleBrandModelsDocument,
          });

          const brand = previousData.vehicleBrandModels?.find(
            (brand) => brand.id === selectedBrand,
          );

          const models = [
            ...(brand?.models?.filter((model) => model.id !== id) || []),
          ];

          const updatedBrand = {
            ...brand,
            models,
          };

          const newBrandList = [
            ...previousData.vehicleBrandModels?.filter(
              (brand) => brand.id !== selectedBrand,
            ),
            updatedBrand,
          ];

          getClient().writeQuery({
            query: GetVehicleBrandModelsDocument,
            data: {
              vehicleBrandModels: newBrandList,
            },
          });

          // @ts-ignore
          getClient().queryManager.broadcastQueries();
        }
      },
    });
  };

  const deleteBrand = async (id: string) => {
    return vehicleDeleteBrandMutation({
      variables: {
        id,
      },
      update: (proxy, mutationResult) => {
        const deleted = mutationResult.data.deleteVehicleBrand?.deleted;

        if (deleted) {
          const previousData = getClient().readQuery<
            GetVehicleBrandModelsQuery,
            GetVehicleBrandModelsQueryVariables
          >({
            query: GetVehicleBrandModelsDocument,
          });

          const newData = _.cloneDeep([
            ...previousData.vehicleBrandModels.filter(
              (brand) => brand.id !== id,
            ),
          ]);

          getClient().writeQuery({
            query: GetVehicleBrandModelsDocument,
            data: {
              vehicleBrandModels: newData,
            },
          });

          // @ts-ignore
          getClient().queryManager.broadcastQueries();
        }
      },
    });
  };

  const vehicleBrandModels =
    vehicleBrandModelsQueryResult.data?.vehicleBrandModels || [];

  const brands =
    vehicleBrandModels.find((brand) => brand.id === selectedBrand)?.models ||
    [];

  return (
    <Drawer
      width={'30%'}
      visible={props.visible}
      onClose={props.onClose}
      closable={true}
      maskClosable={true}
      destroyOnClose={true}
      title={'Gestionar marcas y modelos'}
    >
      {props.visible && (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              marginBottom: '2em',
            }}
          >
            <Button
              icon={<LegacyIcon type={'plus'} />}
              type={'primary'}
              onClick={() => setCreateBrand(true)}
            >
              Crear marca
            </Button>
          </div>
          <Table
            onRow={(record, index) => {
              return {
                onClick: (e: any) => {
                  e.preventDefault();
                  setSelectedBrand(record.id);
                },
              };
            }}
            columns={[
              {
                title: 'Marca',
                key: 'brand',
                dataIndex: 'brand',
                render: (_1, record) => {
                  if (record.id === selectedBrand) {
                    return <b>{record.brand}</b>;
                  } else {
                    return record.brand;
                  }
                },
              },
              {
                title: '',
                key: 'actions',
                render: (
                  _: any,
                  record: GetVehicleBrandModelsQuery['vehicleBrandModels'][number],
                ) => {
                  return (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        size={'small'}
                        onClick={() => {
                          setSelectedBrand(record.id);
                          setUpdateBrand(true);
                        }}
                      >
                        Editar
                      </Button>
                      <Button
                        onClick={() => {
                          deleteBrand(record.id);
                        }}
                        size={'small'}
                        danger
                        style={{ marginLeft: '15px' }}
                      >
                        Eliminar
                      </Button>
                    </div>
                  );
                },
              },
            ]}
            dataSource={[...vehicleBrandModels]}
          />
          <Divider />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              marginBottom: '2em',
            }}
          >
            <Button
              icon={<LegacyIcon type={'plus'} />}
              type={'primary'}
              disabled={!selectedBrand}
              onClick={() => setCreateModel(true)}
            >
              Crear modelo
            </Button>
          </div>
          <Table
            title={() =>
              vehicleBrandModels.find(
                (brand) => brand.id === selectedBrand,
              ) && (
                <h3>{`Modelos de ${
                  vehicleBrandModels.find((brand) => brand.id === selectedBrand)
                    ?.brand
                }`}</h3>
              )
            }
            rowKey={(record) => record.id}
            columns={[
              {
                title: 'Modelo',
                key: 'brand',
                dataIndex: 'name',
              },
              {
                title: '',
                key: 'actions',
                render: (
                  _: any,
                  record: GetVehicleBrandModelsQuery['vehicleBrandModels'][number]['models'][number],
                ) => {
                  return (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        size={'small'}
                        onClick={() => {
                          setSelectedModel(record.id);
                          setUpdateModel(true);
                        }}
                      >
                        Editar
                      </Button>
                      <Button
                        onClick={() => deleteModel(record.id)}
                        size={'small'}
                        danger
                        style={{ marginLeft: '15px' }}
                      >
                        Eliminar
                      </Button>
                    </div>
                  );
                },
              },
            ]}
            dataSource={[...brands]}
          />
          <VehicleCreateBrand
            visible={createBrand}
            onClose={() => {
              setCreateBrand(false);
            }}
          />
          <UpdateBrand
            visible={updateBrand}
            onClose={() => setUpdateBrand(false)}
            brandId={selectedBrand}
          />
          <VehicleCreateModel
            brandId={selectedBrand}
            visible={createModel}
            onClose={() => {
              setCreateModel(false);
            }}
          />
          <VehicleUpdateModel
            visible={updateModel}
            onClose={() => {
              setUpdateModel(false);
            }}
            modelId={selectedModel}
          />
        </>
      )}
    </Drawer>
  );
};

export default VehicleBrandModelsDrawer;
